/**
 * LoginVersionFooter (T29 / T29.2) — the pre-auth version line below the invite
 * text, reframed as a SUPPORT IDENTIFIER.
 *
 * Default (everyone, signed out): the support line — `<platform> · 1.0.<build> ·
 * <channel> · <freshness>` (D2). Plainly visible, no gesture. This REPLACES the
 * old "Version 1.0" — a user with a problem reads this line to support and the
 * engineer pinpoints the exact build. On web the freshness segment is included
 * (`✓ up to date` / `⟳ Update available`, or omitted when it can't check); on
 * native there is no freshness segment yet (Play check is a follow-up).
 *
 * A deliberate 5-tap on the line REVEALS the developer detail — a 2-column grid
 * (`label | value`) (D3). No login required (D11). The label is its OWN grid
 * column (`grid-template-columns: max-content 1fr`), never space-padding, so a
 * long value wraps INSIDE its own column and the label column never shears; the
 * value cell is `min-width: 0` + `overflow-wrap: anywhere`. Each component row is
 * a value cell (`1.0.<build> · <channel> · <freshness>`) plus a muted meta cell
 * (`sha · date`) beneath it.
 *
 * Web reveal:    Web row + Server row.
 * Native reveal: Mobile row + Server row (Mobile and Web collapse to one row —
 *                they're bundled from one commit, D5). A `⚠ Web` row appears
 *                ONLY on real drift (the native shell's build ≠ the running web
 *                bundle's build — dev live-reload), flagged with a warning.
 * Server row carries its OWN build date (D7); a quiet em dash when unreachable.
 *
 * T29.1 — native (Capacitor) shell. On a native platform the installed APK is
 * the app; `useNativeAppVersion` supplies its platform/version/build. Every
 * Capacitor import stays in apps/web (CP1–CP4); `useNativeAppVersion → null` on
 * web, where everything behaves as the web path.
 */

import { useRef, useState, type CSSProperties } from 'react';
import {
  componentValue,
  deriveEnv,
  freshnessLabel,
  metaLine,
  serverValue,
  supportLine,
  versionString,
  SERVER_UNREACHABLE,
} from '../lib/appVersion';
import { useFreshness } from '../lib/useFreshness';
import { useVersionDisplay } from '../lib/useVersionDisplay';
import { useServerVersion } from '../lib/useServerVersion';
import { useNativeAppUpdate } from '../lib/useNativeAppUpdate';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

const TAP_REVEAL_COUNT = 5;

/** Title-case the platform for the everyone-view (`web` → `Web`, `android` → `Android`). */
function platformLabel(platform: string): string {
  if (platform.length === 0) return platform;
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

const footerStyle: CSSProperties = {
  margin: 0,
  marginTop: 'var(--space-2)',
  textAlign: 'center',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-primary)',
  cursor: 'default',
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

// Revealed state: a left-aligned, 2-column grid (label | value). Column 1 is the
// row label (`Web`/`Mobile`/`Server`/`⚠ Web`), `max-content` so every label
// aligns and the column never shears; column 2 (`1fr`) holds the value and wraps
// inside the viewport. The value cell's `minWidth: 0` lets the grid column
// actually shrink and `overflowWrap`/`wordBreak` break a long value mid-token, so
// a wrapped value stays indented under its own column and no row runs off the
// right edge on a narrow phone (D3).
const detailBlockStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  columnGap: 'var(--space-3)',
  rowGap: 'var(--space-1)',
  textAlign: 'left',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-mono, monospace)',
  lineHeight: 1.5,
  maxWidth: '100%',
  boxSizing: 'border-box',
};

// Label cell (column 1): left-aligned, never wraps — it's a short fixed word.
const detailLabelStyle: CSSProperties = {
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

// Value cell (column 2): left-aligned and wraps. `minWidth: 0` is required or the
// grid column refuses to shrink below its content and a long value overflows the
// phone width.
const detailValueStyle: CSSProperties = {
  textAlign: 'left',
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

// Muted meta cell (the `sha · date` line under a value). Same column-2 wrapping;
// dimmed so the version headline reads first.
const detailMetaStyle: CSSProperties = {
  ...detailValueStyle,
  color: 'var(--color-text-muted)',
};

const refreshLinkStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'var(--color-accent, var(--color-text-primary))',
  textDecoration: 'underline',
  cursor: 'pointer',
};

export function LoginVersionFooter(): JSX.Element {
  const server = useServerVersion();
  const freshness = useFreshness();
  // T29.2.4 — ONE decision site (D2). `useVersionDisplay` keys live-vs-frozen on
  // `import.meta.env.DEV` alone (D1) and returns the running build, its channel,
  // the optional native-shell identity, and the precomputed drift. The footer
  // holds NO mode inference of its own.
  const version = useVersionDisplay();
  // The running web bundle's stamp — drives the Web row's version / sha / date /
  // channel (and the drift + meta source on native).
  const webBuild = version.build;
  // `native-shell` is the only kind that renders the Mobile row; `live-web` (incl.
  // local live-reload inside the shell) and `baked-web` render the Web row (D3).
  const isNative = version.kind === 'native-shell';
  const nativeVersion = version.native;
  // T27.2.9 D4 — native Mobile-row freshness word ("is a newer build on my Play
  // track?"). Inert off native; the launch/resume nudge owns the ACTION (open
  // Play) — this only drives the resting word.
  const nativeUpdate = useNativeAppUpdate();
  const [revealed, setRevealed] = useState(false);
  const tapCount = useRef(0);

  const onTap = (): void => {
    tapCount.current += 1;
    if (tapCount.current >= TAP_REVEAL_COUNT) setRevealed(true);
  };

  const env = deriveEnv(API_BASE_URL);
  // Freshness word. On native (T27.2.9 D4) it answers "newer build on my Play
  // track?" from the update check — `✓ up to date` / `⟳ Update available`, or
  // omitted until a check resolves / when it couldn't run (a non-null sentinel
  // only when `checked`, so freshnessLabel never raises a false signal). On web
  // it's the deploy-freshness word (D6), still '' when /version.json couldn't be
  // read (local dev / offline). The native word is display-only — no Refresh
  // affordance; the Play-Store nudge owns the action.
  const freshnessText = isNative
    ? freshnessLabel(nativeUpdate.updateAvailable, nativeUpdate.checked ? 'native' : null)
    : freshnessLabel(freshness.isStale, freshness.latestBuild);
  const showStale = !isNative && freshness.isStale;

  // Everyone-view support line (default, no gesture). On native the platform +
  // channel come from the native shell + the bundle's channel; on web from the
  // running bundle. `Web · 1.0.142 · production · ✓ up to date`.
  const line = isNative
    ? supportLine(
        platformLabel(nativeVersion!.platform),
        { semver: nativeVersion!.version, build: nativeVersion!.build },
        __APP_BUILD__.channel,
        freshnessText,
      )
    : supportLine(platformLabel('web'), webBuild, webBuild.channel, freshnessText);

  // Native drift: the installed shell's build differs from the running web
  // bundle's build (dev live-reload). Precomputed by the hook (D5); only the
  // `native-shell` kind can carry it.
  const hasDrift = version.hasDrift;

  return (
    <p style={footerStyle} data-testid="login-version" onClick={onTap}>
      {revealed ? (
        <span style={detailBlockStyle} data-testid="login-version-detail">
          {isNative ? (
            <>
              {/* Mobile row — the installed APK (Mobile + Web collapse to one
                  row; they're bundled from one commit, D5). The freshness word
                  (T27.2.9 D4) answers "newer build on my Play track?"; omitted
                  until a check resolves. */}
              <span style={detailLabelStyle}>Mobile</span>
              <span style={detailValueStyle} data-testid="login-version-mobile">
                {componentValue(
                  { semver: nativeVersion!.version, build: nativeVersion!.build },
                  __APP_BUILD__.channel,
                  freshnessText,
                )}
              </span>
              <span style={detailLabelStyle} aria-hidden="true" />
              <span style={detailMetaStyle}>
                {metaLine(__APP_BUILD__.sha, __APP_BUILD__.date)}
              </span>
              {/* Drift only — the running web bundle is newer than the shell. */}
              {hasDrift && (
                <>
                  <span style={detailLabelStyle} data-testid="login-version-drift">
                    ⚠ Web
                  </span>
                  <span style={detailValueStyle}>
                    {`${versionString(__APP_BUILD__)} · ${__APP_BUILD__.channel} — newer than the app`}
                  </span>
                </>
              )}
              <span style={detailLabelStyle}>Server</span>
              {server ? (
                <>
                  <span style={detailValueStyle} data-testid="login-version-server">
                    {serverValue(env, server)}
                  </span>
                  <span style={detailLabelStyle} aria-hidden="true" />
                  <span style={detailMetaStyle}>{metaLine(server.sha, server.date)}</span>
                </>
              ) : (
                <span style={detailValueStyle} data-testid="login-version-server">
                  {SERVER_UNREACHABLE}
                </span>
              )}
            </>
          ) : (
            <>
              {/* Web row — the running bundle. Version · channel · freshness. On
                  local this reads the live stamp (current commit, no restart). */}
              <span style={detailLabelStyle}>Web</span>
              <span style={detailValueStyle} data-testid="login-version-web">
                {componentValue(webBuild, webBuild.channel, freshnessText)}
              </span>
              <span style={detailLabelStyle} aria-hidden="true" />
              <span style={detailMetaStyle}>
                {metaLine(webBuild.sha, webBuild.date)}
              </span>
              <span style={detailLabelStyle}>Server</span>
              {server ? (
                <>
                  <span style={detailValueStyle} data-testid="login-version-server">
                    {serverValue(env, server)}
                  </span>
                  <span style={detailLabelStyle} aria-hidden="true" />
                  <span style={detailMetaStyle}>{metaLine(server.sha, server.date)}</span>
                </>
              ) : (
                <span style={detailValueStyle} data-testid="login-version-server">
                  {SERVER_UNREACHABLE}
                </span>
              )}
            </>
          )}
        </span>
      ) : (
        <span data-testid="login-version-label">{line}</span>
      )}

      {showStale && (
        <span data-testid="login-version-stale" style={{ display: 'block' }}>
          🔄 new version —{' '}
          <button
            type="button"
            style={refreshLinkStyle}
            onClick={(e) => {
              e.stopPropagation();
              window.location.reload();
            }}
          >
            Refresh
          </button>
        </span>
      )}
    </p>
  );
}
